import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { NodeViewProps } from '@tiptap/react';
import type { MutableRefObject } from 'react';

import { Alert, Box, Button, Flex, Text } from '@chakra-ui/react';
import { Node } from '@tiptap/core';
import '@tiptap/markdown';
import { NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { useEffect, useMemo, useState } from 'react';
import { FiEdit2 } from 'react-icons/fi';

import {
  formatMarkdownCountdown,
  normalizeCountdownBlockData,
  parseMarkdownCountdownConfig,
  serializeMarkdownCountdownBlock,
  serializeMarkdownCountdownInnerConfig,
  stringifyMarkdownCountdownConfig,
  type CountdownBlockData,
  type MarkdownCountdownEditValues,
  NP_COUNTDOWN_TAG,
} from './markdown-countdown';

interface MarkdownCountdownNodeOptions {
  contextRef: MutableRefObject<MarkdownEditorContextValue>;
}

interface MarkdownCountdownNodeAttrs {
  data: CountdownBlockData;
  rawConfig: string;
  parseError: string | null;
}

function normalizeCountdownAttrs(attrs: unknown): MarkdownCountdownNodeAttrs {
  const rec =
    attrs && typeof attrs === 'object'
      ? (attrs as Record<string, unknown>)
      : {};
  return {
    data: normalizeCountdownBlockData(rec.data),
    rawConfig: typeof rec.rawConfig === 'string' ? rec.rawConfig : '',
    parseError: typeof rec.parseError === 'string' ? rec.parseError : null,
  };
}

function countdownDataToEditValues(
  data: CountdownBlockData,
): MarkdownCountdownEditValues {
  return {
    type: data.type,
    name: data.name,
    targetAt: data.targetAt,
    cron: data.cron,
    displayUnit: data.displayUnit,
  };
}

function CountdownSegments({
  attrs,
  canEdit,
  onEdit,
}: {
  attrs: MarkdownCountdownNodeAttrs;
  canEdit: boolean;
  onEdit: () => void;
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(new Date());
    }, 30 * 1000);
    return () => {
      window.clearInterval(id);
    };
  }, []);

  const countdown = useMemo(
    () => formatMarkdownCountdown(attrs.data, now),
    [attrs.data, now],
  );
  const rightPalette = countdown.completed ? 'green.500' : 'blue.500';

  return (
    <Flex className="markdown-countdown-node__pill" align="stretch">
      <Box className="markdown-countdown-node__segment">
        <Text fontSize="sm" fontWeight="medium">
          {attrs.data.name || 'Unnamed'}
        </Text>
      </Box>
      <Box
        className="markdown-countdown-node__segment markdown-countdown-node__segment--value"
        bg={rightPalette}
      >
        <Text fontSize="sm" fontWeight="semibold" color="white">
          {countdown.primaryText}
        </Text>
      </Box>
      <Button
        className="markdown-countdown-node__edit"
        type="button"
        size="sm"
        variant="solid"
        colorPalette="gray"
        aria-label="Edit countdown"
        onMouseDown={(event) => {
          event.preventDefault();
        }}
        onClick={onEdit}
        disabled={!canEdit}
      >
        <FiEdit2 />
      </Button>
    </Flex>
  );
}

function MarkdownCountdownNodeView({
  extension,
  node,
  selected,
  updateAttributes,
}: NodeViewProps) {
  const { contextRef } = extension.options as MarkdownCountdownNodeOptions;
  const context = contextRef.current;
  const attrs = normalizeCountdownAttrs(node.attrs);

  const handleEdit = () => {
    context.onRequestCountdownEdit?.({
      mode: 'edit',
      initialValues: countdownDataToEditValues(attrs.data),
      onCommit: async (next) => {
        const normalized = normalizeCountdownBlockData(next);
        updateAttributes({
          data: normalized,
          rawConfig: stringifyMarkdownCountdownConfig(normalized),
          parseError: null,
        });
      },
    });
  };

  return (
    <NodeViewWrapper
      as="span"
      className={
        selected
          ? 'markdown-countdown-node markdown-countdown-node--selected'
          : 'markdown-countdown-node'
      }
      contentEditable={false}
      data-drag-handle={false}
    >
      <CountdownSegments
        attrs={attrs}
        canEdit={Boolean(context.onRequestCountdownEdit)}
        onEdit={handleEdit}
      />

      {attrs.parseError ? (
        <Alert.Root status="warning" mt={3} borderRadius="md">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>Invalid countdown config</Alert.Title>
            <Alert.Description>{attrs.parseError}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      ) : null}
    </NodeViewWrapper>
  );
}

export const MarkdownCountdownNode = Node.create<MarkdownCountdownNodeOptions>({
  name: 'npCountdown',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addOptions() {
    return {
      contextRef: { current: null as never },
    };
  },

  addAttributes() {
    return {
      data: {
        default: normalizeCountdownBlockData({}),
      },
      rawConfig: {
        default: '',
      },
      parseError: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: NP_COUNTDOWN_TAG,
        getAttrs: (element) => {
          if (!(element instanceof HTMLElement)) return false;

          const parsed = parseMarkdownCountdownConfig(
            element.textContent ?? '',
          );
          return {
            data: parsed.data,
            rawConfig: parsed.rawConfig,
            parseError: parsed.parseError,
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = normalizeCountdownAttrs(HTMLAttributes);
    return [NP_COUNTDOWN_TAG, serializeMarkdownCountdownInnerConfig(attrs)];
  },

  renderMarkdown(node) {
    const attrs = normalizeCountdownAttrs(node?.attrs);
    return serializeMarkdownCountdownBlock(attrs);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MarkdownCountdownNodeView);
  },
});
